import React from 'react';
import { Navigate, RouteObject, useLocation } from 'react-router-dom';

import ProtectedRouteV3 from '../../v3/ProtectedRouteV3';
import DashboardV3 from '../../v3/Dashboard';
import MandatesListV3 from '../../v3/MandatesList';
import MandateDetailV3 from '../../v3/MandateDetail';
import ClientsListV3 from '../../v3/ClientsList';
import ClientDetailV3 from '../../v3/ClientDetail';
import TombstonesListV3 from '../../v3/TombstonesList';
import TombstoneDetailV3 from '../../v3/TombstoneDetail';
import EmployeesListV3 from '../../v3/EmployeesList';
import EmployeeDetailV3 from '../../v3/EmployeeDetail';
import DocumentsListV3 from '../../v3/DocumentsList';
import DocumentDetailV3 from '../../v3/DocumentDetail';
import PayslipsListV3 from '../../v3/PayslipsList';
import Irp5sListV3 from '../../v3/Irp5sList';
import PerformanceReviewsListV3 from '../../v3/PerformanceReviewsList';
import ExpenseClaimsListV3 from '../../v3/ExpenseClaimsList';
import TimesheetsListV3 from '../../v3/TimesheetsList';
import ProspectsListV3 from '../../v3/ProspectsList';

// V3 redesign is now the DEFAULT UI, mounted at the canonical top-level paths
// (/dashboard, /mandates, ...).  Surfaces not yet re-skinned (HR, funds,
// events, contacts, precedents, admin, etc.) keep their existing legacy routes
// under /core, /admin and /human-resources, and the pre-cutover dashboard +
// full legacy navigation are preserved at /dashboard-legacy.

const v3Routes: RouteObject[] = [
  {
    path: 'dashboard',
    element: (
      <ProtectedRouteV3>
        <DashboardV3 />
      </ProtectedRouteV3>
    )
  },
  {
    path: 'mandates',
    element: (
      <ProtectedRouteV3>
        <MandatesListV3 />
      </ProtectedRouteV3>
    )
  },
  {
    path: 'mandates/:id',
    element: (
      <ProtectedRouteV3>
        <MandateDetailV3 />
      </ProtectedRouteV3>
    )
  },
  {
    path: 'clients',
    element: (
      <ProtectedRouteV3>
        <ClientsListV3 />
      </ProtectedRouteV3>
    )
  },
  {
    path: 'clients/:id',
    element: (
      <ProtectedRouteV3>
        <ClientDetailV3 />
      </ProtectedRouteV3>
    )
  },
  {
    path: 'tombstones',
    element: (
      <ProtectedRouteV3>
        <TombstonesListV3 />
      </ProtectedRouteV3>
    )
  },
  {
    path: 'tombstones/:id',
    element: (
      <ProtectedRouteV3>
        <TombstoneDetailV3 />
      </ProtectedRouteV3>
    )
  },
  {
    path: 'employees',
    element: (
      <ProtectedRouteV3>
        <EmployeesListV3 />
      </ProtectedRouteV3>
    )
  },
  {
    path: 'employees/:id',
    element: (
      <ProtectedRouteV3>
        <EmployeeDetailV3 />
      </ProtectedRouteV3>
    )
  },
  {
    path: 'documents',
    element: (
      <ProtectedRouteV3>
        <DocumentsListV3 />
      </ProtectedRouteV3>
    )
  },
  {
    path: 'documents/:id',
    element: (
      <ProtectedRouteV3>
        <DocumentDetailV3 />
      </ProtectedRouteV3>
    )
  },
  // --- HR phase 1: the three document lockers -----------------------------
  // These sit at top-level paths; the legacy add/edit forms they link out to
  // keep their own /human-resources/* routes, so nothing collides.
  {
    path: 'payslips',
    element: (
      <ProtectedRouteV3>
        <PayslipsListV3 />
      </ProtectedRouteV3>
    )
  },
  {
    path: 'irp5s',
    element: (
      <ProtectedRouteV3>
        <Irp5sListV3 />
      </ProtectedRouteV3>
    )
  },
  {
    path: 'performance-reviews',
    element: (
      <ProtectedRouteV3>
        <PerformanceReviewsListV3 />
      </ProtectedRouteV3>
    )
  },
  // --- HR phase 2 ----------------------------------------------------------
  {
    path: 'expense-claims',
    element: (
      <ProtectedRouteV3>
        <ExpenseClaimsListV3 />
      </ProtectedRouteV3>
    )
  },
  {
    path: 'timesheets',
    element: (
      <ProtectedRouteV3>
        <TimesheetsListV3 />
      </ProtectedRouteV3>
    )
  },
  // --- HR phase 4: recruitment ---------------------------------------------
  // HR-only: a prospect belongs to nobody in particular, so there is no
  // self-service view of this surface.
  {
    path: 'prospects',
    element: (
      <ProtectedRouteV3 hrPrivilegedRequired>
        <ProspectsListV3 />
      </ProtectedRouteV3>
    )
  }
];

// Backwards-compat: the parallel-rollout URLs used a `-v3` suffix.  Forward any
// of those (including :id and query string) to the canonical clean path so old
// bookmarks and in-flight tabs keep working after the cutover.
const LegacyV3Redirect = () => {
  const { pathname, search } = useLocation();
  return <Navigate to={pathname.replace('-v3', '') + search} replace />;
};

const legacyV3Redirects: RouteObject[] = [
  'dashboard-v3',
  'mandates-v3',
  'mandates-v3/:id',
  'clients-v3',
  'clients-v3/:id',
  'tombstones-v3',
  'tombstones-v3/:id',
  'employees-v3',
  'employees-v3/:id',
  'documents-v3',
  'documents-v3/:id'
].map((path) => ({ path, element: <LegacyV3Redirect /> }));

export default [...v3Routes, ...legacyV3Redirects];
